/**
 * @fileoverview Azure Function Health State Hook
 * @description Owns catalog loading, probe scheduling, persistence, and dashboard state.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { azureFunctionHealthApi } from '../services/azureFunctionHealthApi';
import {
  DASHBOARD_FILTERS,
  DEFAULT_DOMAIN,
  DEFAULT_DOMAIN_POD_MAP,
  DEFAULT_POD,
  HEALTH_STORAGE_KEYS,
  HEALTH_ENVIRONMENTS,
  INITIAL_PROBE_STAGGER_MS,
  MAX_INITIAL_PROBE_CONCURRENCY,
  PROBE_KINDS,
  PROBE_REFRESH_INTERVALS,
} from '../constants';

/**
 * Read a JSON object from local storage without preventing dashboard startup.
 * @param {string} storageKey - Local storage key.
 * @returns {Object} Parsed object or an empty object.
 */
function readStoredObject(storageKey) {
  try {
    const parsed = JSON.parse(localStorage.getItem(storageKey) || '{}');
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

function hasStoredValue(storageKey) {
  try {
    return localStorage.getItem(storageKey) !== null;
  } catch {
    return false;
  }
}

/**
 * Remove a local-storage value without blocking dashboard startup.
 * @param {string} storageKey - Local storage key.
 * @returns {void}
 */
function removeStoredValue(storageKey) {
  try {
    localStorage.removeItem(storageKey);
  } catch {
    // Browser storage is optional; live dashboard state remains available.
  }
}

/**
 * Persist a JSON object while allowing probes to continue when storage is blocked.
 * @param {string} storageKey - Local storage key.
 * @param {Object} value - Serializable value.
 * @returns {boolean} Whether the value was persisted.
 */
function writeStoredObject(storageKey, value) {
  try {
    localStorage.setItem(storageKey, JSON.stringify(value));
    return true;
  } catch {
    // Browser storage is optional; live dashboard state remains available.
    return false;
  }
}

// Positions 18, 19, and 30 were removed from the old catalog; never infer a
// replacement from the current response order. (Created or modified by CoPilot AI Agent)
const LEGACY_SCENARIO_IDS = {
  0: 'user-to-person-sync',
  1: 'planned-activity-to-crew-availability',
  2: 'transaction-table-watch-sync',
  3: 'crew-position-qualification-status-disabled',
  4: 'jcte-crew-availability-sync',
  5: 'fox-to-assignments',
  6: 'qualifications-to-jcte',
  7: 'calculate-release-to-jcte',
  8: 'training-events-and-assignments-to-jcte',
  9: 'training-events-feed-to-jcte',
  10: 'crew-bidding-details-to-navblue',
  11: 'planned-absences-to-navblue-sync',
  12: 'carry-over-rosters-to-fox',
  14: 'hcm-to-person-sync',
  15: 'hcm-to-person-sync',
  16: 'sync-flight-logbook-events-from-dap',
  17: 'cornerstone-to-fox',
  20: 'person-to-users-sync',
  21: 'crew-availability-to-basic-event',
  22: 'apis-to-fox',
  23: 'flight-log-book',
  24: 'person-to-users-sync',
  25: 'crew-feeds-to-rainmaker',
  26: 'crew-position-qualification-status',
  27: 'crew-status-assignment-sync',
  28: 'trips-to-activity-based-events',
  29: 'user-quals-to-crew-quals',
};

function findLegacyScenario(scenarios, legacyPosition) {
  const expectedScenarioId = LEGACY_SCENARIO_IDS[legacyPosition];
  return scenarios.find((candidate) => (
    (candidate.legacyId !== undefined
      && candidate.legacyId !== null
      && String(candidate.legacyId) === legacyPosition)
    || (expectedScenarioId && candidate.id === expectedScenarioId)
  ));
}

function migrateLegacyStoredObjects(scenarios) {
  const selectedEnvironments = readStoredObject(HEALTH_STORAGE_KEYS.SELECTED_ENVIRONMENTS);
  const failureNotices = readStoredObject(HEALTH_STORAGE_KEYS.FAILURE_NOTICES);
  const hasLegacyEnvironments = hasStoredValue(HEALTH_STORAGE_KEYS.LEGACY_SELECTED_ENVIRONMENTS);
  const hasLegacyNotices = hasStoredValue(HEALTH_STORAGE_KEYS.LEGACY_FAILURE_NOTICES);

  if (!hasLegacyEnvironments && !hasLegacyNotices) {
    return { selectedEnvironments, failureNotices };
  }

  // Translate recognized positional keys to stable scenario IDs. (Created or modified by CoPilot AI Agent)
  const legacyEnvironments = readStoredObject(HEALTH_STORAGE_KEYS.LEGACY_SELECTED_ENVIRONMENTS);
  Object.entries(legacyEnvironments).forEach(([position, environment]) => {
    if (!/^(0|[1-9]\d*)$/.test(position) || !HEALTH_ENVIRONMENTS.includes(environment)) {
      return;
    }
    const scenario = findLegacyScenario(scenarios, position);
    if (scenario?.id && !/^\d+$/.test(scenario.id) && !Object.hasOwn(selectedEnvironments, scenario.id)) {
      selectedEnvironments[scenario.id] = environment;
    }
  });

  const legacyFailureNotices = readStoredObject(HEALTH_STORAGE_KEYS.LEGACY_FAILURE_NOTICES);
  Object.entries(legacyFailureNotices).forEach(([key, notice]) => {
    const match = /^(0|[1-9]\d*):(head|health):(TEST|QA|PROD)$/.exec(key);
    if (!match || typeof notice !== 'string' || !notice) {
      return;
    }
    const scenario = findLegacyScenario(scenarios, match[1]);
    if (!scenario?.id || /^\d+$/.test(scenario.id)) {
      return;
    }
    const migratedKey = `${scenario.id}:${match[2]}:${match[3]}`;
    if (!Object.hasOwn(failureNotices, migratedKey)) {
      failureNotices[migratedKey] = notice;
    }
  });

  const environmentsWritten = writeStoredObject(
    HEALTH_STORAGE_KEYS.SELECTED_ENVIRONMENTS,
    selectedEnvironments,
  );
  const noticesWritten = writeStoredObject(
    HEALTH_STORAGE_KEYS.FAILURE_NOTICES,
    failureNotices,
  );
  if (environmentsWritten && noticesWritten) {
    removeStoredValue(HEALTH_STORAGE_KEYS.LEGACY_SELECTED_ENVIRONMENTS);
    removeStoredValue(HEALTH_STORAGE_KEYS.LEGACY_FAILURE_NOTICES);
  }

  return { selectedEnvironments, failureNotices };
}

/**
 * Create initial state for one endpoint type.
 * @param {string|null} failureNotice - Previously persisted failure notice.
 * @param {Object|null} result - Latest cached endpoint result.
 * @returns {Object} Endpoint state.
 */
function createEndpointState(failureNotice = null, result = null) {
  return {
    isLoading: false,
    result,
    failureNotice,
    nextScheduledRunAt: null,
  };
}

/**
 * Create a completed result for a probe that is unavailable in the selected state.
 * @param {string} probeKind - head or health.
 * @param {string} reason - Why the probe was skipped.
 * @returns {Object} Skipped endpoint result.
 */
function createSkippedResult(probeKind, reason) {
  return {
    status: 'skipped',
    statusCode: null,
    bodyPreview: reason,
    checkedAt: formatUtcTimestamp(new Date()),
    probeKind,
  };
}

/**
 * Format a date using the source dashboard's UTC timestamp convention.
 * @param {Date} date - Date to format.
 * @returns {string} UTC timestamp.
 */
export function formatUtcTimestamp(date) {
  return date.toISOString().replace('T', ' ').replace(/\.\d{3}Z$/, ' UTC');
}

/**
 * Build the persistence key for one scenario, endpoint kind, and environment.
 * @param {string} scenarioId - Stable scenario identifier.
 * @param {string} probeKind - head or health.
 * @param {string} environment - Selected environment.
 * @returns {string} Persistence key.
 */
function failureNoticeKey(scenarioId, probeKind, environment) {
  return `${scenarioId}:${probeKind}:${environment}`;
}

/**
 * Determine whether a scenario exposes the requested endpoint kind.
 * @param {Object} widget - Dashboard scenario widget.
 * @param {string} probeKind - head or health.
 * @returns {boolean} Whether the endpoint exists.
 */
export function endpointExists(widget, probeKind) {
  return probeKind === PROBE_KINDS.HEAD
    ? widget.headEndpointExists
    : widget.healthEndpointExists;
}

function getSkippedReason(widget, environment, probeKind) {
  if (!endpointExists(widget, probeKind)) {
    return null;
  }
  if (!widget.probeEnabled) {
    return 'Probe disabled for this scenario.';
  }
  if (widget.probeDisabledEnvironments.includes(environment)) {
    return `Probe not available in ${environment}.`;
  }
  return null;
}

function getInitialEndpointResult(widget, environment, probeKind) {
  const cachedResult = widget.latestResults?.[environment]?.[probeKind];
  if (cachedResult) {
    return cachedResult;
  }
  const skippedReason = getSkippedReason(widget, environment, probeKind);
  return skippedReason ? createSkippedResult(probeKind, skippedReason) : null;
}

function parseUtcTimestamp(timestamp) {
  if (!timestamp) {
    return null;
  }
  const parsedTime = Date.parse(timestamp.replace(' UTC', 'Z').replace(' ', 'T'));
  return Number.isNaN(parsedTime) ? null : parsedTime;
}

function getNextRefreshDelay(result, probeKind) {
  const interval = PROBE_REFRESH_INTERVALS[probeKind];
  // Queue first-time probes immediately. (Created or modified by CoPilot AI Agent)
  if (!result) {
    return 0;
  }
  const checkedAt = parseUtcTimestamp(result?.checkedAt);
  if (!checkedAt) {
    return interval;
  }
  const elapsedMilliseconds = Date.now() - checkedAt;
  if (elapsedMilliseconds < 0) {
    return interval;
  }
  return Math.max(0, interval - elapsedMilliseconds);
}

/**
 * Determine whether a scenario exposes both required endpoints.
 * @param {Object} widget - Dashboard scenario widget.
 * @returns {boolean} Whether the scenario is compliant.
 */
export function isEndpointCompliant(widget) {
  return widget.headEndpointExists && widget.healthEndpointExists;
}

export function isPartiallyCompliant(widget) {
  return widget.headEndpointExists !== widget.healthEndpointExists;
}

export function isNotCompliant(widget) {
  return !widget.headEndpointExists && !widget.healthEndpointExists;
}

/**
 * Determine whether completed endpoint results make a widget healthy overall.
 * A widget must contain at least one healthy result and no unhealthy result;
 * skipped-only widgets remain outside the Healthy filter.
 * @param {Object} widget - Dashboard scenario widget.
 * @returns {boolean} Whether the widget currently has a healthy result state.
 */
export function isWidgetHealthy(widget) {
  const completedResults = [
    widget.headEndpointExists ? widget.head.result : null,
    widget.healthEndpointExists ? widget.health.result : null,
  ].filter(Boolean);
  return completedResults.some((result) => result.status === 'healthy')
    && completedResults.every((result) => result.status !== 'unhealthy');
}

// Created or modified by CoPilot AI Agent
function legacyDomainOptions(domainPodMap) {
  return Object.entries(domainPodMap).map(([name, pods]) => ({
    id: name,
    name,
    pods: pods.map((pod) => ({ id: pod, name: pod })),
  }));
}

/**
 * Manage the complete Azure Function Health dashboard lifecycle.
 * @returns {Object} Dashboard state and user actions.
 */
export function useAzureFunctionHealth() {
  const [widgets, setWidgets] = useState([]);
  const [selectedDashboardFilter, setSelectedDashboardFilter] = useState(DASHBOARD_FILTERS.ALL);
  const [domains, setDomains] = useState(() => legacyDomainOptions(DEFAULT_DOMAIN_POD_MAP));
  const [selectedDomain, setSelectedDomainState] = useState(DEFAULT_DOMAIN);
  const [selectedPod, setSelectedPodState] = useState(DEFAULT_POD);
  // Created or modified by CoPilot AI Agent
  const [podNotifyIds, setPodNotifyIds] = useState([]);
  const [savingPodId, setSavingPodId] = useState(null);
  const [isCatalogLoading, setIsCatalogLoading] = useState(true);
  const [catalogError, setCatalogError] = useState('');
  const widgetsRef = useRef([]);
  const refreshTimersRef = useRef(new Map());
  const requestGenerationsRef = useRef(new Map());
  const queuedProbeRunsRef = useRef([]);
  const queuedProbeRunsInFlightRef = useRef(new Set());
  const queuedProbeRunTimerTasksRef = useRef(new Map());
  const queuedProbeRunSequenceRef = useRef(0);
  const runEndpointRef = useRef(null);
  const selectedDomainRef = useRef(DEFAULT_DOMAIN);
  const selectedPodRef = useRef(DEFAULT_POD);
  const failureNoticesRef = useRef(readStoredObject(HEALTH_STORAGE_KEYS.FAILURE_NOTICES));
  const selectedEnvironmentsRef = useRef(readStoredObject(HEALTH_STORAGE_KEYS.SELECTED_ENVIRONMENTS));

  const setSelectedDomain = useCallback((domainId) => {
    const pods = domains.find((domain) => domain.id === domainId)?.pods || [];
    const nextPod = pods[0]?.id ?? '';
    selectedDomainRef.current = domainId;
    selectedPodRef.current = nextPod;
    setSelectedDomainState(domainId);
    setSelectedPodState(nextPod);
  }, [domains]);

  const setSelectedPod = useCallback((pod) => {
    selectedPodRef.current = pod;
    setSelectedPodState(pod);
  }, []);

  /**
   * Update widget state and its synchronous reference together.
   * @param {Function} transformWidgets - State transformation callback.
   * @returns {void}
   */
  const updateWidgets = useCallback((transformWidgets) => {
    setWidgets((currentWidgets) => {
      const nextWidgets = transformWidgets(currentWidgets);
      widgetsRef.current = nextWidgets;
      return nextWidgets;
    });
  }, []);

  /**
   * Schedule one endpoint to run after the requested delay.
   * @param {string} scenarioId - Stable scenario identifier.
   * @param {string} probeKind - head or health.
   * @param {number} delayMilliseconds - Delay before execution.
   * @param {number} expectedGeneration - Widget generation that owns the schedule.
   * @returns {void}
   */
  const removeQueuedProbeRun = useCallback((timerKey) => {
    queuedProbeRunsRef.current = queuedProbeRunsRef.current.filter((task) => (
      `${task.scenarioId}:${task.probeKind}` !== timerKey
    ));
  }, []);

  const clearScheduledEndpointRun = useCallback((timerKey) => {
    const existingTimer = refreshTimersRef.current.get(timerKey);
    if (existingTimer) {
      clearTimeout(existingTimer);
    }
    const queuedTaskId = queuedProbeRunTimerTasksRef.current.get(timerKey);
    if (queuedTaskId !== undefined) {
      queuedProbeRunsInFlightRef.current.delete(queuedTaskId);
      queuedProbeRunTimerTasksRef.current.delete(timerKey);
    }
    refreshTimersRef.current.delete(timerKey);
  }, []);

  const flushQueuedProbeRuns = useCallback(() => {
    if (queuedProbeRunsInFlightRef.current.size >= MAX_INITIAL_PROBE_CONCURRENCY) {
      return;
    }

    while (queuedProbeRunsRef.current.length > 0 && queuedProbeRunsInFlightRef.current.size < MAX_INITIAL_PROBE_CONCURRENCY) {
      const task = queuedProbeRunsRef.current.shift();
      const timerKey = `${task.scenarioId}:${task.probeKind}`;

      queuedProbeRunsInFlightRef.current.add(task.id);
      const timer = setTimeout(async () => {
        try {
          queuedProbeRunTimerTasksRef.current.delete(timerKey);
          await runEndpointRef.current?.(task.scenarioId, task.probeKind, task.expectedGeneration);
        } finally {
          queuedProbeRunsInFlightRef.current.delete(task.id);
          if (refreshTimersRef.current.get(timerKey) === timer) {
            refreshTimersRef.current.delete(timerKey);
          }
          flushQueuedProbeRuns();
        }
      }, task.delayMilliseconds);
      refreshTimersRef.current.set(timerKey, timer);
      queuedProbeRunTimerTasksRef.current.set(timerKey, task.id);
    }
  }, []);

  const scheduleEndpointRun = useCallback((
    scenarioId,
    probeKind,
    delayMilliseconds,
    expectedGeneration = requestGenerationsRef.current.get(scenarioId) || 0,
  ) => {
    if ((requestGenerationsRef.current.get(scenarioId) || 0) !== expectedGeneration) {
      return;
    }

    const timerKey = `${scenarioId}:${probeKind}`;
    clearScheduledEndpointRun(timerKey);
    removeQueuedProbeRun(timerKey);

    const widget = widgetsRef.current.find((candidate) => candidate.id === scenarioId);
    if (!widget || !endpointExists(widget, probeKind)) {
      return;
    }

    const scheduledAt = formatUtcTimestamp(new Date(Date.now() + delayMilliseconds));
    updateWidgets((currentWidgets) => {
      if ((requestGenerationsRef.current.get(scenarioId) || 0) !== expectedGeneration) {
        return currentWidgets;
      }
      return currentWidgets.map((candidate) => (
        candidate.id === scenarioId
          ? { ...candidate, [probeKind]: { ...candidate[probeKind], nextScheduledRunAt: scheduledAt } }
          : candidate
      ));
    });

    if (delayMilliseconds <= 0) {
      const queuedTask = {
        id: queuedProbeRunSequenceRef.current,
        scenarioId,
        probeKind,
        delayMilliseconds: queuedProbeRunsRef.current.length * INITIAL_PROBE_STAGGER_MS,
        expectedGeneration,
      };
      queuedProbeRunSequenceRef.current += 1;
      queuedProbeRunsRef.current.push(queuedTask);
      flushQueuedProbeRuns();
      return;
    }

    const timer = setTimeout(
      () => runEndpointRef.current?.(scenarioId, probeKind, expectedGeneration),
      delayMilliseconds,
    );
    refreshTimersRef.current.set(timerKey, timer);
  }, [clearScheduledEndpointRun, flushQueuedProbeRuns, removeQueuedProbeRun, updateWidgets]);

  const scheduleWidgetRefreshes = useCallback((widget, expectedGeneration) => {
    Object.values(PROBE_KINDS).forEach((probeKind) => {
      if (!endpointExists(widget, probeKind)) {
        return;
      }
      const result = widget[probeKind].result;
      if (result?.status === 'skipped') {
        return;
      }
      scheduleEndpointRun(
        widget.id,
        probeKind,
        getNextRefreshDelay(result, probeKind),
        expectedGeneration,
      );
    });
  }, [scheduleEndpointRun]);

  /**
   * Execute one endpoint probe and schedule its next cadence.
   * @param {string} scenarioId - Stable scenario identifier.
   * @param {string} probeKind - head or health.
   * @param {number} expectedGeneration - Optional generation supplied by a timer.
   * @returns {Promise<void>} Completion promise.
   */
  const runEndpoint = useCallback(async (scenarioId, probeKind, expectedGeneration) => {
    const requestGeneration = requestGenerationsRef.current.get(scenarioId) || 0;
    if (expectedGeneration !== undefined && expectedGeneration !== requestGeneration) {
      return;
    }

    const widget = widgetsRef.current.find((candidate) => candidate.id === scenarioId);
    const selectedEnvironment = widget?.selectedEnvironment;
    if (!widget || !endpointExists(widget, probeKind) || widget[probeKind].isLoading) {
      return;
    }

    const skipReason = getSkippedReason(widget, selectedEnvironment, probeKind);
    if (skipReason) {
      const timerKey = `${scenarioId}:${probeKind}`;
      const skippedResult = createSkippedResult(probeKind, skipReason);
      clearScheduledEndpointRun(timerKey);
      removeQueuedProbeRun(timerKey);
      updateWidgets((currentWidgets) => currentWidgets.map((candidate) => (
        candidate.id === scenarioId
          ? {
            ...candidate,
            [probeKind]: {
              ...candidate[probeKind],
              result: skippedResult,
              failureNotice: null,
              nextScheduledRunAt: null,
            },
            latestResults: {
              ...candidate.latestResults,
              [selectedEnvironment]: {
                ...(candidate.latestResults?.[selectedEnvironment] || {}),
                [probeKind]: skippedResult,
              },
            },
          }
          : candidate
      )));
      return;
    }

    updateWidgets((currentWidgets) => currentWidgets.map((candidate) => (
      candidate.id === scenarioId
        ? { ...candidate, [probeKind]: { ...candidate[probeKind], isLoading: true } }
        : candidate
    )));

    try {
      const result = await azureFunctionHealthApi.probeEndpoint(
        scenarioId,
        selectedEnvironment,
        probeKind,
      );
      if ((requestGenerationsRef.current.get(scenarioId) || 0) !== requestGeneration) {
        return;
      }
      const noticeKey = failureNoticeKey(scenarioId, probeKind, selectedEnvironment);
      if (result.status === 'healthy') {
        delete failureNoticesRef.current[noticeKey];
        writeStoredObject(HEALTH_STORAGE_KEYS.FAILURE_NOTICES, failureNoticesRef.current);
      }
      updateWidgets((currentWidgets) => currentWidgets.map((candidate) => {
        if (candidate.id !== scenarioId) {
          return candidate;
        }

        let failureNotice = candidate[probeKind].failureNotice;
        if (result.status === 'unhealthy' && !failureNotice) {
          failureNotice = `${probeKind.toUpperCase()} failed in ${selectedEnvironment} at ${result.checkedAt}.`;
          failureNoticesRef.current[noticeKey] = failureNotice;
          writeStoredObject(HEALTH_STORAGE_KEYS.FAILURE_NOTICES, failureNoticesRef.current);
        } else if (result.status === 'healthy') {
          failureNotice = null;
        }
        return {
          ...candidate,
          [probeKind]: { ...candidate[probeKind], result, failureNotice },
          latestResults: {
            ...candidate.latestResults,
            [selectedEnvironment]: {
              ...(candidate.latestResults?.[selectedEnvironment] || {}),
              [probeKind]: result,
            },
          },
        };
      }));
    } catch (error) {
      if ((requestGenerationsRef.current.get(scenarioId) || 0) !== requestGeneration) {
        return;
      }
      const checkedAt = formatUtcTimestamp(new Date());
      const result = {
        status: 'unhealthy',
        statusCode: error.status ?? null,
        bodyPreview: error.message || 'Request failed.',
        checkedAt,
      };
      const noticeKey = failureNoticeKey(scenarioId, probeKind, selectedEnvironment);
      const failureNotice = `${probeKind.toUpperCase()} failed in ${selectedEnvironment} at ${checkedAt}.`;
      failureNoticesRef.current[noticeKey] = failureNotice;
      writeStoredObject(HEALTH_STORAGE_KEYS.FAILURE_NOTICES, failureNoticesRef.current);
      updateWidgets((currentWidgets) => currentWidgets.map((candidate) => (
        candidate.id === scenarioId
          ? {
            ...candidate,
            [probeKind]: { ...candidate[probeKind], result, failureNotice },
            latestResults: {
              ...candidate.latestResults,
              [selectedEnvironment]: {
                ...(candidate.latestResults?.[selectedEnvironment] || {}),
                [probeKind]: result,
              },
            },
          }
          : candidate
      )));
    } finally {
      if ((requestGenerationsRef.current.get(scenarioId) || 0) === requestGeneration) {
        updateWidgets((currentWidgets) => currentWidgets.map((candidate) => (
          candidate.id === scenarioId
            ? { ...candidate, [probeKind]: { ...candidate[probeKind], isLoading: false } }
            : candidate
        )));
        scheduleEndpointRun(
          scenarioId,
          probeKind,
          PROBE_REFRESH_INTERVALS[probeKind],
          requestGeneration,
        );
      }
    }
  }, [clearScheduledEndpointRun, removeQueuedProbeRun, scheduleEndpointRun, updateWidgets]);

  runEndpointRef.current = runEndpoint;

  useEffect(() => {
    let isMounted = true;
    const refreshTimers = refreshTimersRef.current;
    const requestGenerations = requestGenerationsRef.current;
    const queuedProbeRunsInFlight = queuedProbeRunsInFlightRef.current;
    const queuedProbeRunTimerTasks = queuedProbeRunTimerTasksRef.current;

    /** Load the catalog and start each available endpoint's initial run. */
    async function loadScenarioCatalog() {
      try {
        const catalog = await azureFunctionHealthApi.getCatalog();
        const scenarios = catalog.scenarios ?? [];
        if (!isMounted) {
          return;
        }

        const migratedStorage = migrateLegacyStoredObjects(scenarios);
        selectedEnvironmentsRef.current = migratedStorage.selectedEnvironments;
        failureNoticesRef.current = migratedStorage.failureNotices;

        // Created or modified by CoPilot AI Agent
        const nextDomains = Array.isArray(catalog.domains)
          ? catalog.domains
          : legacyDomainOptions(catalog.domainPodMap || DEFAULT_DOMAIN_POD_MAP);
        const nextDomain = nextDomains.find((domain) => domain.id === selectedDomainRef.current)
          || nextDomains[0];
        const nextPods = nextDomain?.pods || [];
        const nextPod = nextPods.some((pod) => pod.id === selectedPodRef.current)
          ? selectedPodRef.current
          : nextPods[0]?.id ?? '';
        selectedDomainRef.current = nextDomain?.id ?? '';
        selectedPodRef.current = nextPod;
        setDomains(nextDomains);
        setSelectedDomainState(nextDomain?.id ?? '');
        setSelectedPodState(nextPod);
        setPodNotifyIds(catalog.podNotifyIds || []);

        const loadedWidgets = scenarios.map((scenario) => {
          // Created or modified by CoPilot AI Agent
          const selectedEnvironment = 'TEST';
          requestGenerationsRef.current.set(scenario.id, 0);
          return {
            ...scenario,
            domain: scenario.domain || DEFAULT_DOMAIN,
            pod: scenario.pod || DEFAULT_POD,
            selectedEnvironment,
            head: createEndpointState(
              failureNoticesRef.current[failureNoticeKey(scenario.id, PROBE_KINDS.HEAD, selectedEnvironment)] || null,
              getInitialEndpointResult(scenario, selectedEnvironment, PROBE_KINDS.HEAD),
            ),
            health: createEndpointState(
              failureNoticesRef.current[failureNoticeKey(scenario.id, PROBE_KINDS.HEALTH, selectedEnvironment)] || null,
              getInitialEndpointResult(scenario, selectedEnvironment, PROBE_KINDS.HEALTH),
            ),
          };
        });
        widgetsRef.current = loadedWidgets;
        setWidgets(loadedWidgets);
        // Default to favorites only when the selected pod contains one. (Created or modified by CoPilot AI Agent)
        setSelectedDashboardFilter(
          loadedWidgets.some((widget) => (
            widget.isFavorite
            && (widget.domainId ?? widget.domain) === selectedDomainRef.current
            && (widget.podId ?? widget.pod) === selectedPodRef.current
          ))
            ? DASHBOARD_FILTERS.FAVORITES
            : DASHBOARD_FILTERS.ALL,
        );
        setCatalogError('');

        loadedWidgets.forEach((widget) => {
          const requestGeneration = requestGenerationsRef.current.get(widget.id) || 0;
          scheduleWidgetRefreshes(widget, requestGeneration);
        });
      } catch (error) {
        if (isMounted) {
          setCatalogError(error.message || 'Unable to load Azure Function health scenarios.');
        }
      } finally {
        if (isMounted) {
          setIsCatalogLoading(false);
        }
      }
    }

    loadScenarioCatalog();
    return () => {
      isMounted = false;
      requestGenerations.forEach((generation, scenarioId) => {
        requestGenerations.set(scenarioId, generation + 1);
      });
      refreshTimers.forEach((timer) => clearTimeout(timer));
      refreshTimers.clear();
      queuedProbeRunsRef.current = [];
      queuedProbeRunsInFlight.clear();
      queuedProbeRunTimerTasks.clear();
    };
  }, [scheduleWidgetRefreshes]);

  /**
   * Change one widget's environment and restart its endpoint schedules.
   * @param {string} scenarioId - Stable scenario identifier.
   * @param {string} environment - Newly selected environment.
   * @returns {void}
   */
  const setWidgetEnvironment = useCallback((scenarioId, environment) => {
    // Invalidate every in-flight request and timer owned by the previous environment. (Created or modified by CoPilot AI Agent)
    const nextGeneration = (requestGenerationsRef.current.get(scenarioId) || 0) + 1;
    requestGenerationsRef.current.set(scenarioId, nextGeneration);
    selectedEnvironmentsRef.current[scenarioId] = environment;
    writeStoredObject(HEALTH_STORAGE_KEYS.SELECTED_ENVIRONMENTS, selectedEnvironmentsRef.current);
    let updatedWidget = null;
    updateWidgets((currentWidgets) => currentWidgets.map((widget) => {
      if (widget.id !== scenarioId) {
        return widget;
      }
      updatedWidget = {
        ...widget,
        selectedEnvironment: environment,
        head: createEndpointState(
          failureNoticesRef.current[failureNoticeKey(scenarioId, PROBE_KINDS.HEAD, environment)] || null,
          getInitialEndpointResult(widget, environment, PROBE_KINDS.HEAD),
        ),
        health: createEndpointState(
          failureNoticesRef.current[failureNoticeKey(scenarioId, PROBE_KINDS.HEALTH, environment)] || null,
          getInitialEndpointResult(widget, environment, PROBE_KINDS.HEALTH),
        ),
      };
      return updatedWidget;
    }));
    if (updatedWidget) {
      scheduleWidgetRefreshes(updatedWidget, nextGeneration);
    }
  }, [scheduleWidgetRefreshes, updateWidgets]);

  const setAllWidgetEnvironments = useCallback((environment) => {
    const updatedWidgets = widgetsRef.current.map((widget) => {
      const nextGeneration = (requestGenerationsRef.current.get(widget.id) || 0) + 1;
      requestGenerationsRef.current.set(widget.id, nextGeneration);
      selectedEnvironmentsRef.current[widget.id] = environment;
      return {
        ...widget,
        selectedEnvironment: environment,
        head: createEndpointState(
          failureNoticesRef.current[failureNoticeKey(widget.id, PROBE_KINDS.HEAD, environment)] || null,
          getInitialEndpointResult(widget, environment, PROBE_KINDS.HEAD),
        ),
        health: createEndpointState(
          failureNoticesRef.current[failureNoticeKey(widget.id, PROBE_KINDS.HEALTH, environment)] || null,
          getInitialEndpointResult(widget, environment, PROBE_KINDS.HEALTH),
        ),
      };
    });

    writeStoredObject(HEALTH_STORAGE_KEYS.SELECTED_ENVIRONMENTS, selectedEnvironmentsRef.current);
    updateWidgets(() => updatedWidgets);
    updatedWidgets.forEach((widget) => {
      const requestGeneration = requestGenerationsRef.current.get(widget.id) || 0;
      scheduleWidgetRefreshes(widget, requestGeneration);
    });
  }, [scheduleWidgetRefreshes, updateWidgets]);

  /**
   * Run every endpoint exposed by one widget.
   * @param {string} scenarioId - Stable scenario identifier.
   * @returns {void}
   */
  const runAllForWidget = useCallback((scenarioId) => {
    const widget = widgetsRef.current.find((candidate) => candidate.id === scenarioId);
    if (widget?.headEndpointExists) {
      runEndpoint(scenarioId, PROBE_KINDS.HEAD);
    }
    if (widget?.healthEndpointExists) {
      runEndpoint(scenarioId, PROBE_KINDS.HEALTH);
    }
  }, [runEndpoint]);

  /** Run every endpoint currently exposed by the catalog. */
  const runAllAvailableEndpoints = useCallback(() => {
    widgetsRef.current.filter((widget) => (
      (widget.domainId ?? widget.domain) === selectedDomainRef.current
      && (widget.podId ?? widget.pod) === selectedPodRef.current
    )).forEach((widget) => {
      const requestGeneration = requestGenerationsRef.current.get(widget.id) || 0;
      if (widget.headEndpointExists) {
        scheduleEndpointRun(widget.id, PROBE_KINDS.HEAD, 0, requestGeneration);
      }
      if (widget.healthEndpointExists) {
        scheduleEndpointRun(widget.id, PROBE_KINDS.HEALTH, 0, requestGeneration);
      }
    });
  }, [scheduleEndpointRun]);

  /**
   * Dismiss and remove one persisted failure notice.
   * @param {string} scenarioId - Stable scenario identifier.
   * @param {string} probeKind - head or health.
   * @returns {void}
   */
  const clearFailureNotice = useCallback((scenarioId, probeKind) => {
    const widget = widgetsRef.current.find((candidate) => candidate.id === scenarioId);
    if (!widget) {
      return;
    }
    delete failureNoticesRef.current[failureNoticeKey(scenarioId, probeKind, widget.selectedEnvironment)];
    writeStoredObject(HEALTH_STORAGE_KEYS.FAILURE_NOTICES, failureNoticesRef.current);
    updateWidgets((currentWidgets) => currentWidgets.map((candidate) => (
      candidate.id === scenarioId
        ? { ...candidate, [probeKind]: { ...candidate[probeKind], failureNotice: null } }
        : candidate
    )));
  }, [updateWidgets]);

  const toggleFavorite = useCallback(async (scenarioId) => {
    const widget = widgetsRef.current.find((candidate) => candidate.id === scenarioId);
    if (!widget?.azureFunctionId || widget.isNotifySaving || widget.isFavoriteSaving) {
      return;
    }

    const nextFavoriteState = !widget.isFavorite;
    updateWidgets((currentWidgets) => currentWidgets.map((candidate) => (
      candidate.id === scenarioId
        ? { ...candidate, isFavorite: nextFavoriteState, notify: nextFavoriteState ? candidate.notify : false, isFavoriteSaving: true }
        : candidate
    )));

    try {
      await azureFunctionHealthApi.setFavorite(
        widget.azureFunctionId,
        nextFavoriteState,
      );
    } catch {
      updateWidgets((currentWidgets) => currentWidgets.map((candidate) => (
        candidate.id === scenarioId
          ? { ...candidate, isFavorite: widget.isFavorite, notify: widget.notify }
          : candidate
      )));
    } finally {
      updateWidgets((currentWidgets) => currentWidgets.map((candidate) => (
        candidate.id === scenarioId ? { ...candidate, isFavoriteSaving: false } : candidate
      )));
    }
  }, [updateWidgets]);

  // Created or modified by CoPilot AI Agent
  const setWidgetNotify = useCallback(async (scenarioId, notify) => {
    const widget = widgetsRef.current.find((candidate) => candidate.id === scenarioId);
    if (!widget?.azureFunctionId || !widget.isFavorite || widget.isNotifySaving || widget.isFavoriteSaving) {
      return;
    }
    updateWidgets((currentWidgets) => currentWidgets.map((candidate) => (
      candidate.id === scenarioId
        ? { ...candidate, notify, isNotifySaving: true }
        : candidate
    )));
    try {
      await azureFunctionHealthApi.setNotify(widget.azureFunctionId, notify);
    } catch {
      updateWidgets((currentWidgets) => currentWidgets.map((candidate) => (
        candidate.id === scenarioId ? { ...candidate, notify: widget.notify } : candidate
      )));
    } finally {
      updateWidgets((currentWidgets) => currentWidgets.map((candidate) => (
        candidate.id === scenarioId ? { ...candidate, isNotifySaving: false } : candidate
      )));
    }
  }, [updateWidgets]);

  // Created or modified by CoPilot AI Agent
  const setPodNotify = useCallback(async (notify) => {
    const podId = selectedPodRef.current;
    if (!Number.isInteger(podId) || podId <= 0 || savingPodId !== null) {
      return;
    }
    setSavingPodId(podId);
    setPodNotifyIds((current) => (
      notify ? [...new Set([...current, podId])] : current.filter((id) => id !== podId)
    ));
    try {
      await azureFunctionHealthApi.setPodNotify(podId, notify);
    } catch {
      setPodNotifyIds((current) => (
        notify ? current.filter((id) => id !== podId) : [...new Set([...current, podId])]
      ));
    } finally {
      setSavingPodId(null);
    }
  }, [savingPodId]);

  const selectedDomainPods = domains.find((domain) => domain.id === selectedDomain)?.pods || [];
  const podFilteredWidgets = widgets.filter((widget) => (
    (widget.domainId ?? widget.domain) === selectedDomain
    && (widget.podId ?? widget.pod) === selectedPod
  ));

  const filteredWidgets = podFilteredWidgets.filter((widget) => {
    if (selectedDashboardFilter === DASHBOARD_FILTERS.FAVORITES) {
      return widget.isFavorite;
    }
    if (selectedDashboardFilter === DASHBOARD_FILTERS.FULLY_COMPLIANT) {
      return isEndpointCompliant(widget);
    }
    if (selectedDashboardFilter === DASHBOARD_FILTERS.PARTIAL_COMPLIANT) {
      return isPartiallyCompliant(widget);
    }
    if (selectedDashboardFilter === DASHBOARD_FILTERS.NOT_COMPLIANT) {
      return isNotCompliant(widget);
    }
    if (selectedDashboardFilter === DASHBOARD_FILTERS.HEALTHY) {
      return isWidgetHealthy(widget);
    }
    if (selectedDashboardFilter === DASHBOARD_FILTERS.UNHEALTHY) {
      return (widget.headEndpointExists && widget.head.result?.status === 'unhealthy')
        || (widget.healthEndpointExists && widget.health.result?.status === 'unhealthy');
    }
    if (selectedDashboardFilter === DASHBOARD_FILTERS.SKIPPED) {
      return (widget.headEndpointExists && widget.head.result?.status === 'skipped')
        || (widget.healthEndpointExists && widget.health.result?.status === 'skipped');
    }
    return true;
  });

  const countByStatus = (status) => podFilteredWidgets.reduce((count, widget) => (
    count
    + (widget.headEndpointExists && widget.head.result?.status === status ? 1 : 0)
    + (widget.healthEndpointExists && widget.health.result?.status === status ? 1 : 0)
  ), 0);
  const fullyCompliantCount = podFilteredWidgets.filter(isEndpointCompliant).length;
  const partialCompliantCount = podFilteredWidgets.filter(isPartiallyCompliant).length;
  const notCompliantCount = podFilteredWidgets.filter(isNotCompliant).length;

  return {
    widgets,
    filteredWidgets,
    domains,
    selectedDomain,
    selectedPod,
    podNotify: podNotifyIds.includes(selectedPod),
    isPodNotifySaving: savingPodId !== null,
    setPodNotify,
    selectedDomainPods,
    selectedPodName: selectedDomainPods.find((pod) => pod.id === selectedPod)?.name || '',
    selectedPodEndpointCount: podFilteredWidgets.length,
    setSelectedDomain,
    setSelectedPod,
    selectedDashboardFilter,
    setSelectedDashboardFilter,
    isCatalogLoading,
    catalogError,
    healthyCount: countByStatus('healthy'),
    unhealthyCount: countByStatus('unhealthy'),
    skippedCount: countByStatus('skipped'),
    fullyCompliantCount,
    partialCompliantCount,
    notCompliantCount,
    runEndpoint,
    runAllForWidget,
    runAllAvailableEndpoints,
    setWidgetEnvironment,
    setAllWidgetEnvironments,
    clearFailureNotice,
    toggleFavorite,
    setWidgetNotify,
  };
}
